import {
  ChangeEvent,
  ClipboardEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { useSnackbar } from "src/components/Snackbar/useSnackbar";
import { useSaveChatAttachmentMutation } from "src/hooks/useSaveChatAttachmentMutation";
import {
  getFileParseStatus,
  retryAttachmentParsing,
  uploadChatAttachment,
} from "src/services/backend";
import { v4 as uuid } from "uuid";
import {
  AttachmentSaveError,
  ChatAttachment,
  isImageAttachment,
  MAX_ATTACHMENTS,
  MAX_IMAGE_BYTES,
  MAX_MESSAGE_IMAGE_BYTES,
  readImage,
} from "../attachments";
import { useChatDeviceContext } from "../useChatDeviceContext";
import { CHAT_SUPPORTED_FILE_TYPES } from "../utils";

export function useFileUpload({
  disabled = false,
}: { disabled?: boolean } = {}) {
  const [contentBlocks, setBlocks] = useState<ChatAttachment[]>([]);
  const current = useRef<ChatAttachment[]>([]);
  const [dragOver, setDragOver] = useState(false);
  const dropRef = useRef<HTMLDivElement>(null);
  const dragCounter = useRef(0);
  const { orgId } = useChatDeviceContext();
  const { showSnackbar } = useSnackbar();
  const saveAttachment = useSaveChatAttachmentMutation();
  const setContentBlocks = useCallback((blocks: ChatAttachment[]) => {
    current.current = blocks;
    setBlocks(blocks);
  }, []);
  const patch = (id: string, update: Partial<ChatAttachment>) => {
    setContentBlocks(
      current.current.map((a) => (a.localId === id ? { ...a, ...update } : a))
    );
  };
  const setSaveToEqms = (id: string, saveToEqms: boolean) => {
    const attachment = current.current.find((a) => a.localId === id);
    if (
      !disabled &&
      attachment &&
      !attachment.saveStarted &&
      !attachment.reference?.documentId
    ) {
      patch(id, { saveToEqms });
    }
  };
  const prepareForSend = async () => {
    for (const attachment of current.current) {
      if (!attachment.saveToEqms || attachment.reference?.documentId) continue;
      if (attachment.status !== "ready" || !attachment.reference) {
        throw new AttachmentSaveError(
          `Wait for “${attachment.file.name}” to finish uploading and parsing.`
        );
      }
      // A lost response can hide a completed save. Keep the choice locked and
      // retry with the same file ID; the backend makes that save idempotent.
      patch(attachment.localId, { saveStarted: true });
      try {
        const { documentId } = await saveAttachment.mutateAsync({
          orgId,
          fileId: attachment.reference.id,
        });
        patch(attachment.localId, {
          reference: { ...attachment.reference, documentId },
        });
      } catch {
        throw new AttachmentSaveError(
          `Could not save “${attachment.file.name}” to eQMS. Send again to retry.`
        );
      }
    }
    return current.current;
  };
  const prepare = async (attachment: ChatAttachment) => {
    const { localId, file } = attachment;
    const active = () => current.current.some((a) => a.localId === localId);
    try {
      patch(localId, {
        status: attachment.reference ? "parsing" : "uploading",
        error: undefined,
      });
      const image = isImageAttachment(file);
      const imageContent = image ? await readImage(file) : undefined;
      const imageData = imageContent?.data;
      let reference = attachment.reference;
      if (!reference) {
        reference = (await uploadChatAttachment({ orgId, file })).data;
        if (imageContent)
          reference = { ...reference, imagePixels: imageContent.pixels };
        if (!active()) return;
        patch(localId, { reference, imageData });
      } else if (!image) {
        await retryAttachmentParsing({ orgId, fileId: reference.id });
      }
      if (!active()) return;
      if (image) {
        patch(localId, { status: "ready" });
        return;
      }
      patch(localId, { status: "parsing" });
      const deadline = Date.now() + 6 * 60 * 1000;
      while (active()) {
        const { data } = await getFileParseStatus({
          orgId,
          fileId: reference.id,
        });
        if (!active()) return;
        if (data.status === "SUCCESS") {
          patch(localId, { status: "ready" });
          return;
        }
        if (data.status !== "PENDING")
          throw new Error("Parsing failed. Retry this file.");
        if (Date.now() > deadline)
          throw new Error("Parsing is taking too long. Retry to check again.");
        await new Promise((resolve) => setTimeout(resolve, 2000));
      }
    } catch (error) {
      if (active())
        patch(localId, {
          status: "failed",
          error:
            error instanceof Error
              ? error.message
              : "Upload failed. Retry this file.",
        });
    }
  };
  const processFiles = async (files: File[]) => {
    if (disabled) return;
    const accepted: ChatAttachment[] = [];
    let imageBytes = current.current
      .filter((a) => isImageAttachment(a.file))
      .reduce((sum, a) => sum + a.file.size, 0);
    for (const file of files) {
      const extension = "." + file.name.split(".").pop()?.toLowerCase();
      const image = isImageAttachment(file);
      const limit = image ? MAX_IMAGE_BYTES : 50 * 1024 * 1024;
      if (!CHAT_SUPPORTED_FILE_TYPES.split(",").includes(extension)) {
        showSnackbar(`Unsupported file: ${file.name}`);
        continue;
      }
      if (!file.size || file.size > limit) {
        showSnackbar(
          `${file.name} must be between 1 byte and ${limit / 1024 / 1024} MB.`
        );
        continue;
      }
      if (current.current.length + accepted.length >= MAX_ATTACHMENTS) {
        showSnackbar("Attach at most 10 files per message.");
        break;
      }
      if (image && imageBytes + file.size > MAX_MESSAGE_IMAGE_BYTES) {
        showSnackbar("Images in one message must total at most 20 MB.");
        continue;
      }
      if (image) imageBytes += file.size;
      accepted.push({
        localId: uuid(),
        file,
        status: "uploading",
        saveToEqms: false,
      });
    }
    setContentBlocks([...current.current, ...accepted]);
    await Promise.all(accepted.map(prepare));
  };
  const processRef = useRef(processFiles);
  processRef.current = processFiles;
  useEffect(() => {
    const enter = (e: DragEvent) => {
      if (e.dataTransfer?.types.includes("Files")) {
        dragCounter.current++;
        setDragOver(true);
      }
    };
    const leave = () => {
      if (--dragCounter.current <= 0) {
        dragCounter.current = 0;
        setDragOver(false);
      }
    };
    const over = (e: DragEvent) => {
      if (e.dataTransfer?.types.includes("Files")) e.preventDefault();
    };
    const drop = (e: DragEvent) => {
      e.preventDefault();
      dragCounter.current = 0;
      setDragOver(false);
      if (e.dataTransfer?.files.length)
        void processRef.current(Array.from(e.dataTransfer.files));
    };
    window.addEventListener("dragenter", enter);
    window.addEventListener("dragleave", leave);
    window.addEventListener("dragover", over);
    window.addEventListener("drop", drop);
    return () => {
      window.removeEventListener("dragenter", enter);
      window.removeEventListener("dragleave", leave);
      window.removeEventListener("dragover", over);
      window.removeEventListener("drop", drop);
      current.current = [];
    };
  }, [orgId]);
  useEffect(() => {
    setContentBlocks([]);
  }, [orgId, setContentBlocks]);
  const handleFileUpload = async (event: ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files ?? []);
    event.target.value = "";
    await processFiles(files);
  };
  const handlePaste = async (
    event: ClipboardEvent<HTMLTextAreaElement | HTMLInputElement>
  ) => {
    const files = Array.from(event.clipboardData.items)
      .map((item) => item.getAsFile())
      .filter((file): file is File => !!file);
    if (files.length) {
      event.preventDefault();
      await processFiles(files);
    }
  };
  return {
    contentBlocks,
    setContentBlocks,
    setSaveToEqms,
    prepareForSend,
    dropRef,
    dragOver,
    handleFileUpload,
    handlePaste,
    processFiles,
    removeBlock: (id: string) =>
      setContentBlocks(current.current.filter((a) => a.localId !== id)),
    retryBlock: (id: string) => {
      const attachment = current.current.find((a) => a.localId === id);
      if (attachment?.status === "failed") void prepare(attachment);
    },
    resetBlocks: () => setContentBlocks([]),
  };
}
